import { Router } from '@angular/router';
import { NewsArticleCommunity, NewsArticleListItem } from '@models/news-article';
import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { findAndRemove } from '../../helpers';
import { NewsArticlesService } from '../news-articles.service';
import { addDataToListItem, findAndReplaceByIdWithSort, NewsArticlesStore } from '../news-articles.store';

export const getNewsArticleCommunityHandlingMethods = (injects: {
  store: NewsArticlesStore;
  newsArticlesService: NewsArticlesService;
  router: Router;
}) => {
  const { store, newsArticlesService, router } = injects;

  const selectNewsArticleCommunity = (communityId: string) => {
    patchState(store, { selectedNewsArticleCommunityId: communityId });
  };

  const addNewsArticleCommunities = rxMethod<{ articleId: string; communities: string[] }>(
    pipe(
      tap(() =>
        patchState(store, {
          isSelectedNewsArticleLoading: true,
          isLoading: true,
        }),
      ),
      switchMap(({ articleId, communities }) =>
        newsArticlesService.addCommunities(articleId, communities).pipe(
          tap(newsArticle => {
            patchState(store, {
              selectedNewsArticle: addDataToListItem(newsArticle, store.selectedNewsArticle()),
              isSelectedNewsArticleLoading: false,
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              isLoading: false,
              selectedNewsArticleCommunityId: null,
            });
          }),
          catchError(() => {
            patchState(store, {
              isSelectedNewsArticleLoading: false,
              isLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const updateNewsArticleCommunity = rxMethod<{ articleId: string; communityId: string; community: any }>(
    pipe(
      tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true })),
      switchMap(({ articleId, communityId, community }) =>
        newsArticlesService.updateCommunity(articleId, communityId, community).pipe(
          tap(newsArticle => {
            patchState(store, {
              selectedNewsArticle: addDataToListItem(newsArticle, store.selectedNewsArticle()),
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              isSelectedNewsArticleLoading: false,
              isLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const deleteNewsArticleCommunity = rxMethod<{ articleId: string; communityId: string; isCommunityView: boolean }>(
    pipe(
      tap(() =>
        patchState(store, {
          isSelectedNewsArticleLoading: true,
          isNewsArticlesByCommunityLoading: true,
          isLoading: true,
        }),
      ),
      switchMap(({ articleId, communityId, isCommunityView }) =>
        newsArticlesService.deleteCommunity(articleId, communityId).pipe(
          tap(() => {
            const selectedNewsArticle = store.selectedNewsArticle()!;
            const updatedCommunities = findAndRemove<NewsArticleCommunity>(
              selectedNewsArticle.communities || [],
              item => item.id === communityId,
            );
            patchState(store, {
              selectedNewsArticle: isCommunityView ? null : { ...selectedNewsArticle, communities: updatedCommunities },
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), {
                ...selectedNewsArticle,
                communities: updatedCommunities,
              }),
              newsArticlesByCommunity: findAndRemove<NewsArticleListItem>(store.newsArticlesByCommunity(), item => item.id === articleId),
              isSelectedNewsArticleLoading: false,
              isNewsArticlesByCommunityLoading: false,
              isLoading: false,
            });
            if (isCommunityView) {
              router.navigate(['marketing', 'communities', communityId, 'news-articles']);
            } else {
              router.navigate(['marketing', 'news-articles', 'article', articleId, 'communities']);
            }
          }),
          catchError(() => {
            patchState(store, {
              isSelectedNewsArticleLoading: false,
              isNewsArticlesByCommunityLoading: false,
              isLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const approveNewsArticleCommunity = rxMethod<{ articleId: string; communityId: string }>(
    pipe(
      tap(() =>
        patchState(store, {
          isLoading: true,
          isNewsArticlesByCommunityLoading: true,
          isSelectedNewsArticleLoading: true,
        }),
      ),
      switchMap(({ articleId, communityId }) =>
        newsArticlesService.approveCommunity(articleId, communityId).pipe(
          tap(newsArticle => {
            patchState(store, {
              selectedNewsArticle: { content: store.selectedNewsArticle()?.content ?? null, ...newsArticle },
              isSelectedNewsArticleLoading: false,
              newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), newsArticle),
              newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), newsArticle),
              isLoading: false,
              isNewsArticlesByCommunityLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, {
              isSelectedNewsArticleLoading: false,
              isLoading: false,
              isNewsArticlesByCommunityLoading: false,
            });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  return {
    selectNewsArticleCommunity,
    addNewsArticleCommunities,
    updateNewsArticleCommunity,
    deleteNewsArticleCommunity,
    approveNewsArticleCommunity,
  };
};

export interface NewsArticleCommunityHandlingStoreType {
  readonly selectNewsArticleCommunity: (communityId: string) => void;
  readonly addNewsArticleCommunities: (input: { articleId: string; communities: string[] }) => void;
  readonly updateNewsArticleCommunity: (input: { articleId: string; communityId: string; community: any }) => void;
  readonly deleteNewsArticleCommunity: (input: { articleId: string; communityId: string; isCommunityView: boolean }) => void;
  readonly approveNewsArticleCommunity: (input: { articleId: string; communityId: string }) => void;
}
